import { useRef, useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Sparkle, CircleNotch, Lightning } from "@phosphor-icons/react";
import { streamAI } from "@/lib/api";

export function useAIStream() {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const start = useCallback((payload) => {
    setText("");
    setError(null);
    setDone(null);
    setRunning(true);
    streamAI(payload, {
      onDelta: (d) => setText((t) => t + d),
      onDone: (d) => { setDone(d); setRunning(false); },
      onError: (e) => { setError(e); setRunning(false); },
    });
  }, []);

  const reset = useCallback(() => { setText(""); setError(null); setDone(null); setRunning(false); }, []);
  return { text, running, error, done, start, reset };
}

function renderInline(s, keyPrefix) {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`${keyPrefix}-${i}`} className="font-semibold text-[#0A0D14]">{p.slice(2, -2)}</strong>
      : p
  );
}

export function Markdown({ text }) {
  const lines = text.split("\n");
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const t = line.trim();
        if (t.startsWith("## ")) return <h3 key={i} className="font-heading text-sm font-extrabold uppercase tracking-[0.15em] text-ai mt-5 mb-1">{t.slice(3)}</h3>;
        if (t.startsWith("# ")) return <h3 key={i} className="font-heading text-sm font-extrabold uppercase tracking-[0.15em] text-ai mt-5 mb-1">{t.slice(2)}</h3>;
        if (t.startsWith("### ")) return <h4 key={i} className="font-heading text-sm font-bold mt-3">{t.slice(4)}</h4>;
        if (/^[-*•] /.test(t)) return <div key={i} className="flex gap-2 text-sm leading-relaxed text-[#3a4150]"><span className="text-ai mt-[2px]">▸</span><span>{renderInline(t.replace(/^[-*•] /, ""), i)}</span></div>;
        if (/^\d+\. /.test(t)) return <div key={i} className="flex gap-2 text-sm leading-relaxed text-[#3a4150]"><span className="font-mono text-xs text-ai font-bold mt-[3px]">{t.match(/^\d+/)[0]}.</span><span>{renderInline(t.replace(/^\d+\. /, ""), i)}</span></div>;
        if (t === "") return <div key={i} className="h-1" />;
        return <p key={i} className="text-sm leading-relaxed text-[#3a4150]">{renderInline(t, i)}</p>;
      })}
    </div>
  );
}

export default function AIDrawer({ open, onClose, title, subtitle, text, running, error, decision, onApply, applying }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [text]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 border-l border-ai/20" data-testid="ai-drawer">
        <div className="h-full flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5E7EB] bg-gradient-to-r from-ai/[0.06] to-transparent">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg bg-ai/10 flex items-center justify-center">
                <Sparkle size={18} weight="duotone" className="text-ai" />
              </span>
              <div>
                <SheetTitle className="font-heading text-lg font-extrabold tracking-tight text-left" data-testid="ai-drawer-title">{title}</SheetTitle>
                <SheetDescription className="text-xs text-[#525866] text-left">{subtitle}</SheetDescription>
              </div>
              {running && (
                <span className="ml-auto flex items-center gap-1.5 text-xs font-bold text-ai" data-testid="ai-drawer-thinking">
                  <span className="h-2 w-2 rounded-full bg-ai animate-pulse" />
                  Claude Opus 4.7 thinking
                </span>
              )}
            </div>
          </SheetHeader>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5" data-testid="ai-drawer-content">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="ai-drawer-error">{error}</div>
            )}
            {!error && !text && running && (
              <div className="flex items-center gap-3 text-sm text-[#525866] py-8 justify-center">
                <CircleNotch size={20} className="animate-spin text-ai" />
                Analyzing your ad data…
              </div>
            )}
            {text && <Markdown text={text} />}
            {running && text && <span className="inline-block h-4 w-[3px] bg-ai animate-pulse ml-0.5 align-middle" />}
          </div>
          {decision && !running && !error && (
            <div className="px-6 py-4 border-t border-ai/20 bg-ai/[0.05] flex items-center justify-between gap-4" data-testid="ai-decision-bar">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-ai">Claude's Verdict</div>
                <div className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]" data-testid="ai-decision-verdict">{decision}</div>
              </div>
              {(decision === "SCALE" || decision === "PAUSE") && onApply ? (
                <Button onClick={onApply} disabled={applying} data-testid="apply-ai-decision-button"
                  className="rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-10 px-5 transition-transform hover:-translate-y-[1px] active:scale-[0.98]">
                  <Lightning size={14} weight="fill" className="mr-1.5 text-[#0052FF]" />
                  {applying ? "Applying…" : decision === "SCALE" ? "Apply · Scale Budget +20%" : "Apply · Pause Campaign"}
                </Button>
              ) : (
                <span className="text-xs text-[#525866] text-right max-w-[200px]">Manual optimization — follow the steps above</span>
              )}
            </div>
          )}
          <div className="px-6 py-3 border-t border-[#E5E7EB] text-[11px] text-[#8a90a0] flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-ai" />
            Powered by Claude Opus 4.7 · Recommendations based on last 14 days of data
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
